import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { TagColor } from "app/data/common/tagColor";
import Tag from "ui-components/molecules/tag/tag";
import _ from "lodash";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import moment from "moment";
import { ReactComponent as ArriveLoadIcon } from "../../../images/loaded_truck.svg";
import { ReactComponent as DoorIcon } from "../../../images/door.svg";
import { ReactComponent as CloseDoorIcon } from "../../../images/door_lock.svg";
import { ReactComponent as CloseDoorGreyIcon } from "../../../images/door_lock_grey.svg";

const columnHelper = createColumnHelper<any>();

/** Normalizes door code: treats null, undefined, and empty/whitespace strings as missing. */
const getEffectiveDoorCode = (row: { assignedDoorCode?: string | null; doorNumber?: string | null } | null | undefined): string | null => {
  const v = row?.assignedDoorCode ?? row?.doorNumber;
  if (v == null || v === "") return null;
  const s = String(v).trim();
  return s === "" ? null : v;
};

/** Shared helper for unloading progress parsing and calculation. */
const getUnloadingProgress = (rowData: { piecesUnloaded?: number; piecesCount?: string | number | null } | null | undefined) => {
  const piecesUnloaded = rowData?.piecesUnloaded;
  const piecesCountRaw = rowData?.piecesCount;
  const piecesCount = piecesCountRaw != null && piecesCountRaw !== "" ? Number(piecesCountRaw) : 0;
  const showUnloadingProgress = typeof piecesUnloaded === "number" && piecesCount > 0;
  const unloadingPercent = showUnloadingProgress ? Math.min(100, Math.round((piecesUnloaded / piecesCount) * 100)) : 0;
  return { piecesCount, showUnloadingProgress, unloadingPercent };
};

const getStatusColor = (status: string) => {
  if (status === 'ARRIVED') {
    return TagColor.GREEN;
  }
  if (status === 'UNLOADING') {
    return TagColor.PURPLE;
  }
  if (status === 'LATE' || status === 'ALERT') {
    return TagColor.BLUE;
  }
  if (status === 'INBOUND' || status === 'IN_TRANSIT') {
    return TagColor.BLUE;
  }
  if (status === 'UNLOADED' || status === 'STAGED') {
    return TagColor.GREY;
  }
  return TagColor.GREY;
}

const formatStatusLabel = (value?: string) => {
  if (!value) return "-";
  return value
    .toString()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatTrailerFilterLabel = (key: string | number | undefined | null): string => {
  if (key == null || key === "") return "—";
  return `TRL-${key}`;
};

const formatWeightFilterLabel = (key: string | number | undefined | null): string => {
  if (key == null || key === "") return "—";
  const n = Number(key);
  return Number.isFinite(n) ? n.toLocaleString("en-US") : String(key);
};

const coerceArrivalDateTime = (value: unknown): string => {
  if (value == null || value === "") return "";
  if (value instanceof Date) {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.toISOString() : "";
  }
  if (typeof value === "object") {
    const v = value as Record<string, unknown>;
    if (v.dateTime != null && String(v.dateTime).trim() !== "") return String(v.dateTime);
    if (v.date != null && v.time != null) return `${v.date} ${v.time}`;
    if (v.date != null && String(v.date).trim() !== "") return String(v.date);
    return "";
  }
  const asString = String(value).trim();
  return asString === "" || asString === "[object Object]" ? "" : asString;
};

const getActualArrivalRaw = (row: {
  actualArrivalDateTime?: unknown;
} | null | undefined): string => coerceArrivalDateTime(row?.actualArrivalDateTime);

const getEstimatedArrivalRaw = (row: {
  estimatedArrivalDateTime?: unknown;
  actualArrivalDateTime?: unknown;
  arrivalDate?: string;
  arrivalTime?: string;
} | null | undefined): string => {
  const estimated = coerceArrivalDateTime(row?.estimatedArrivalDateTime);
  if (estimated) return estimated;
  if (getActualArrivalRaw(row)) return "";
  return row?.arrivalDate && row?.arrivalTime ? `${row.arrivalDate} ${row.arrivalTime}` : "";
};

const formatDateTimeForFilter = (raw: string): string => {
  if (!raw) return "";
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format("MM/DD/YY hh:mm A") : raw;
};

const renderDateTimeCell = (raw: string) => {
  if (!raw) return "-";
  const parsed = moment(raw);
  if (!parsed.isValid()) return raw;
  return (
    <div style={{ whiteSpace: "nowrap" }}>
      {parsed.format("MM/DD/YY")} &nbsp; {parsed.format("hh:mm A")}
    </div>
  );
};

const formatArrivalFilterLabel = (key: string | number | undefined | null): string => {
  if (key == null || key === "" || String(key) === "[object Object]") return "—";
  const parsed = moment(String(key));
  return parsed.isValid() ? parsed.format("MM/DD/YY hh:mm A") : String(key);
};

const formatOriginDate = (value?: string | null): string => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YY") : String(value);
};

const sortFacetEntriesNumericKey = (
  a: [string | number | undefined | null, unknown],
  b: [string | number | undefined | null, unknown]
) => {
  const k1 = String(a[0] ?? "");
  const k2 = String(b[0] ?? "");
  const n1 = k1 === "" ? NaN : Number(k1);
  const n2 = k2 === "" ? NaN : Number(k2);
  const ok1 = !Number.isNaN(n1);
  const ok2 = !Number.isNaN(n2);
  if (!ok1 && !ok2) return k1.localeCompare(k2);
  if (!ok1) return 1;
  if (!ok2) return -1;
  return n1 - n2;
};

const DATE_TIME_PARSE_FORMATS = ["MM/DD/YY hh:mm A", "MM/DD/YY", moment.ISO_8601] as const;

const parseDateTimeSortValue = (value: unknown): number => {
  const raw = String(value ?? "").trim();
  if (!raw) return NaN;
  return moment(raw, DATE_TIME_PARSE_FORMATS as unknown as string[], true).valueOf();
};

const compareDateTimeSortValues = (a: unknown, b: unknown): number => {
  const t1 = parseDateTimeSortValue(a);
  const t2 = parseDateTimeSortValue(b);
  const ok1 = Number.isFinite(t1);
  const ok2 = Number.isFinite(t2);
  if (!ok1 && !ok2) return String(a ?? "").localeCompare(String(b ?? ""));
  if (!ok1) return 1;
  if (!ok2) return -1;
  return t1 - t2;
};

const sortFacetEntriesDateKey = (
  a: [string | number | undefined | null, unknown],
  b: [string | number | undefined | null, unknown]
) => compareDateTimeSortValues(a[0], b[0]);

/** Chronological sort for columns whose accessor is a display-formatted datetime string. */
const sortDateTimeColumn = (rowA: { getValue: (columnId: string) => unknown }, rowB: { getValue: (columnId: string) => unknown }, columnId: string) =>
  compareDateTimeSortValues(rowA.getValue(columnId), rowB.getValue(columnId));

const selectFilterMeta = { filterVariant: "select" as const };

/** Column id and label for column visibility selector */
export const INBOUND_FREIGHTS_COLUMN_OPTIONS: { id: string; label: string }[] = [
  { id: "status", label: "Status" },
  { id: "arrival", label: "Arrival" },
  { id: "eta", label: "ETA" },
  { id: "doorNumber", label: "Door" },
  { id: "nextStep", label: "Next Step" },
  { id: "trailerNumber", label: "Trailer" },
  { id: "type", label: "Type" },
  { id: "manifestNumber", label: "Manifest" },
  { id: "originTerminal", label: "Origin Terminal" },
  { id: "originDate", label: "Dispatched Date" },
  { id: "probillCount", label: "Probills" },
  { id: "piecesCount", label: "Pieces" },
  { id: "lbs", label: "Weight (lbs)" },
];

export const INBOUND_FREIGHTS_COLUMNS_HIDDEN_BY_DEFAULT: string[] = [];

export const getInboundFreightsColumn = (
  onAssignDoorClick?: (rowData: any) => void,
  onArriveLoadClick?: (rowData: any) => void,
  onCloseDoorClick?: (rowData: any) => void,
  onManifestClick?: (rowData: any) => void
) => [
  columnHelper.accessor((row) => row?.freightStatus || row?.status || "", {
    id: "status",
    header: "Status",
    cell: (props) => {
      const rowData = props.row.original;
      const status = rowData?.freightStatus || rowData?.status || '';
      return (
        <div className="inbound-freight-status-badge" data-status={status}>
          <Tag mods={{ color: getStatusColor(status) }}>
            {formatStatusLabel(status)}
          </Tag>
        </div>
      );
    },
    meta: {
      ...selectFilterMeta,
      filterLabelFormatter: formatStatusLabel,
    },
    filterFn: "equalsString",
    size: 120,
    minSize: 120,
  }),

  columnHelper.accessor(
    (row) => formatDateTimeForFilter(getActualArrivalRaw(row)),
    {
      id: "arrival",
      header: "Arrival",
      cell: ({ row }) => renderDateTimeCell(getActualArrivalRaw(row.original)),
      meta: {
        ...selectFilterMeta,
        filterLabelFormatter: formatArrivalFilterLabel,
        filterSortFn: sortFacetEntriesDateKey,
      },
      filterFn: "equalsString",
      sortingFn: sortDateTimeColumn,
      size: 170,
      minSize: 170,
    }
  ),

  columnHelper.accessor(
    (row) => formatDateTimeForFilter(getEstimatedArrivalRaw(row)),
    {
      id: "eta",
      header: "Due Date",
      cell: ({ row }) => renderDateTimeCell(getEstimatedArrivalRaw(row.original)),
      meta: {
        ...selectFilterMeta,
        filterLabelFormatter: formatArrivalFilterLabel,
        filterSortFn: sortFacetEntriesDateKey,
      },
      filterFn: "equalsString",
      sortingFn: sortDateTimeColumn,
      size: 170,
      minSize: 170,
    }
  ),

  columnHelper.accessor((row) => getEffectiveDoorCode(row), {
    id: "doorNumber",
    header: "Door",
    cell: (props) => {
      const rowData = props.row.original;
      const status = rowData?.freightStatus || rowData?.status;
      const assignedDoorCode = getEffectiveDoorCode(rowData);
      const doorAction = rowData?.doorAction;
      const doorActionLabel = rowData?.doorActionLabel;
      const doorLabel = assignedDoorCode
        ? (() => {
            const normalized = assignedDoorCode.toString().toUpperCase();
            return normalized.startsWith("D-") ? normalized : `D-${normalized}`;
          })()
        : "";
      const selectedDoor: XGSSelectOption | null = assignedDoorCode
        ? { label: doorLabel, value: assignedDoorCode }
        : null;

      if (selectedDoor) {
        const showUnloading = status === 'UNLOADING' || status === 'UNLOADED';
        const showAtDoor = status === 'ARRIVED' && assignedDoorCode;
        const { showUnloadingProgress: baseShowProgress, unloadingPercent } = getUnloadingProgress(rowData);
        const showUnloadingProgress = showUnloading && baseShowProgress;

        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>{selectedDoor.label}</span>
              {(status === 'UNLOADING' || status === 'UNLOADED') && (
                <CloseDoorGreyIcon style={{ width: '14px', height: '14px' }} />
              )}
              {/* TODO: Restore edit door icon when feature is ready */}
            </div>
            {showUnloading ? (
              <div className="inbound-freight-door-unloading">
                {showUnloadingProgress ? (
                  <>
                    <div className="inbound-freight-unloading-progress__header">
                      <span style={{ fontSize: '12px', color: '#777' }}>Unloading</span>
                      <span className="inbound-freight-unloading-progress__percent">
                        {unloadingPercent}%
                      </span>
                    </div>
                    <div className="inbound-freight-unloading-progress__track">
                      <div
                        className="inbound-freight-unloading-progress__bar"
                        style={{ width: `${unloadingPercent}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <span style={{ fontSize: '12px', color: '#777' }}>Unloading</span>
                )}
              </div>
            ) : showAtDoor ? (
              <div style={{ fontSize: '12px', color: '#777' }}>
                At Door
              </div>
            ) : null}
          </div>
        );
      }

      // No door assigned: show doorActionLabel from API (e.g. "Assign Door")
      const showAssignDoor = doorAction === 'ASSIGN_DOOR' || (!assignedDoorCode && (status === 'ARRIVED' || status === 'UNLOADING' || status === 'UNLOADED' || status === 'IN_TRANSIT' || status === 'INBOUND'));
      const linkLabel = doorActionLabel || "Assign Door";

      if (showAssignDoor && (status === 'ARRIVED' || status === 'UNLOADING' || status === 'UNLOADED')) {
        return (
          <div className="inbound-freight-assign-door-cell">
            <button
              type="button"
              className="inbound-freight-assign-door-link"
              onClick={() => onAssignDoorClick?.(rowData)}
            >
              {linkLabel}
            </button>
            {status === 'ARRIVED' && (
              <div style={{ fontSize: '12px', color: '#777' }}>
                At Yard
              </div>
            )}
          </div>
        );
      }

      if (showAssignDoor && (status === 'IN_TRANSIT' || status === 'INBOUND')) {
        return (
          <div className="inbound-freight-assign-door-cell" style={{ fontSize: '14px', color: '#8A8F98', whiteSpace: 'nowrap' }}>
            {linkLabel}
          </div>
        );
      }

      return '-';
    },
    enableColumnFilter: false,
    size: 120,
    minSize: 120,
  }),

  columnHelper.accessor(
    (row) => row?.nextStepActionLabel || row?.nextStepAction || "",
    {
    id: "nextStep",
    header: "Next Step",
    cell: ({ row }) => {
      const rowData = row.original;
      const status = rowData?.freightStatus || rowData?.status || '';
      const doorNumber = getEffectiveDoorCode(rowData);
      const nextStepActionLabel = rowData?.nextStepActionLabel;
      const nextStepAction = rowData?.nextStepAction;
      const nextStepEnabled = rowData?.nextStepEnabled !== false;
      const actions: JSX.Element[] = [];

      // When nextStepEnabled is false and the freight is unloading/unloaded, show informational display (with progress when available)
      if (!nextStepEnabled && (nextStepAction === 'UNLOADING' || status === 'UNLOADING' || status === 'UNLOADED')) {
        const { showUnloadingProgress, unloadingPercent } = getUnloadingProgress(rowData);

        return (
          <div className="inbound-freight-next-step-cell">
            {showUnloadingProgress ? (
              <div className="inbound-freight-door-unloading">
                <div className="inbound-freight-unloading-progress__header">
                  <span style={{ fontSize: '14px', color: '#555' }}>
                    {nextStepActionLabel || "Unloading"}
                  </span>
                  <span className="inbound-freight-unloading-progress__percent">
                    {unloadingPercent}%
                  </span>
                </div>
                <div className="inbound-freight-unloading-progress__track">
                  <div
                    className="inbound-freight-unloading-progress__bar"
                    style={{ width: `${unloadingPercent}%` }}
                  />
                </div>
              </div>
            ) : (
              <span style={{ fontSize: '14px', color: '#555' }}>
                {nextStepActionLabel || "Unloading"}
              </span>
            )}
          </div>
        );
      }

      if (nextStepEnabled && (nextStepAction === "ARRIVE" || (!nextStepAction && (status === 'IN_TRANSIT' || status === 'INBOUND')))) {
        actions.push(
          <button
            key="arrive-load"
            className="inbound-freight-next-step-action"
            onClick={() => onArriveLoadClick?.(rowData)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              background: 'none',
              border: 'none',
              color: '#2F80ED',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0
            }}
          >
            <ArriveLoadIcon style={{ width: '16px', height: '16px' }} />
            {nextStepActionLabel || "Arrive Load"}
          </button>
        );
      }

      if (nextStepEnabled && (nextStepAction === "ASSIGN_DOOR" || (!nextStepAction && status === 'ARRIVED')) && !doorNumber) {
        actions.push(
          <button
            key="assign-door"
            className="inbound-freight-next-step-action"
            onClick={() => onAssignDoorClick?.(rowData)}
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '4px',
              background: 'none',
              border: 'none',
              color: '#2F80ED',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              minWidth: 'min-content'
            }}
          >
            <DoorIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{nextStepActionLabel || "Assign Door"}</span>
          </button>
        );
      }

      if (nextStepEnabled && (nextStepAction === "CLOSE_DOOR" || (!nextStepAction && (status === 'UNLOADING' || status === 'UNLOADED')))) {
        actions.push(
          <button
            key="close-door"
            className="inbound-freight-next-step-action"
            onClick={() => onCloseDoorClick?.(rowData)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              background: 'none',
              border: 'none',
              color: '#2F80ED',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0
            }}
          >
            <CloseDoorIcon style={{ width: '16px', height: '16px' }} />
            {nextStepActionLabel || "Close Door"}
          </button>
        );
      }

      if (actions.length === 0) {
        return '-';
      }

      return (
        <div className="inbound-freight-next-step-cell" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          {actions}
        </div>
      );
    },
    meta: selectFilterMeta,
    filterFn: "equalsString",
    size: 136,
    minSize: 116,
  }),

  columnHelper.accessor("trailerNumber", {
    id: "trailerNumber",
    header: "Trailer",
    cell: (props) => {
      const trailer = props.getValue();
      return (
        <div style={{ whiteSpace: 'nowrap' }}>
          {!_.isEmpty(trailer) ? `TRL-${trailer?.toString()}` : '-'}
        </div>
      );
    },
    meta: {
      ...selectFilterMeta,
      filterLabelFormatter: formatTrailerFilterLabel,
      filterSortFn: sortFacetEntriesNumericKey,
    },
    filterFn: "equalsString",
    size: 110,
    minSize: 100,
  }),

  columnHelper.accessor("type", {
    id: "type",
    header: "Type",
    cell: (props) => {
      return (
        <Tag mods={{ color: TagColor.GREY }}>
          Linehaul
        </Tag>
      );
    },
    enableColumnFilter: false,
    size: 100,
    minSize: 100,
  }),

  columnHelper.accessor("manifestNumber", {
    id: "manifestNumber",
    header: "Manifest",
    cell: (props) => {
      const manifestNumber = props.getValue();
      const rowData = props.row.original;
      if (!manifestNumber) return '-';
      return (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onManifestClick?.(rowData);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onManifestClick?.(rowData);
            }
          }}
        >
          MF-{manifestNumber}
        </span>
      );
    },
    filterFn: "includesString",
    size: 125,
    minSize: 125,
  }),

  columnHelper.accessor(
    (row) => {
      const { originTerminalNumber, originTerminalName, originAirportCode } = row;
      const hasAny = [originTerminalNumber, originTerminalName, originAirportCode].some(
        (v) => v != null && v !== ''
      );
      if (!hasAny) return '';
      const parts = [originAirportCode, originTerminalName].filter(Boolean).join(' - ');
      const suffix = originTerminalNumber != null && originTerminalNumber !== '' ? ` ${originTerminalNumber}` : '';
      return parts + suffix;
    },
    {
    id: "originTerminal",
    header: "Origin Terminal",
    cell: (cellProps) => {
      const displayValue = cellProps.getValue();
      if (!displayValue) return '-';
      return (
        <div className="inbound-freight-origin-terminal-cell">
          {displayValue}
        </div>
      );
    },
    meta: selectFilterMeta,
    filterFn: "equalsString",
    size: 187,
    minSize: 177,
  }),

  columnHelper.accessor(
    (row) => formatOriginDate(row.originDate),
    {
      id: "originDate",
      header: "Dispatched Date",
      cell: (props) => {
        const formatted = props.getValue() || "-";
        return <>
          <div style={{ whiteSpace: 'nowrap' }}>
            {formatted}
          </div>
        </>
      },
      meta: {
        ...selectFilterMeta,
        filterSortFn: sortFacetEntriesDateKey,
      },
      filterFn: "equalsString",
      size: 130,
      minSize: 130,
    }
  ),

  columnHelper.accessor("probillCount", {
    id: "probillCount",
    header: "Probills",
    cell: (props) => !_.isEmpty(props.getValue()) ? props.getValue() : '-',
    meta: {
      ...selectFilterMeta,
      filterSortFn: sortFacetEntriesNumericKey,
    },
    filterFn: "equalsString",
    size: 100,
    minSize: 100,
  }),

  columnHelper.accessor("piecesCount", {
    id: "piecesCount",
    header: "Pieces",
    cell: (props) => !_.isEmpty(props.getValue()) ? props.getValue() : '-',
    meta: {
      ...selectFilterMeta,
      filterSortFn: sortFacetEntriesNumericKey,
    },
    filterFn: "equalsString",
    size: 120,
    minSize: 120,
  }),

  columnHelper.accessor("lbs", {
    id: "lbs",
    header: "Weight (lbs)",
    cell: (props) => props.getValue() ? Number(props.getValue())?.toLocaleString("en-US") : '-',
    meta: {
      ...selectFilterMeta,
      filterLabelFormatter: formatWeightFilterLabel,
      filterSortFn: sortFacetEntriesNumericKey,
    },
    filterFn: "equalsString",
    size: 130,
    minSize: 130,
  }),
];
